import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Hammer, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import LocationPicker from "../components/LocationPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArtisanProjectNew({ user }) {
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [data, setData] = useState({
    title: "",
    description: "",
    category: "",
    budget_min: "",
    budget_max: "",
    address: "",
    latitude: null,
    longitude: null,
    deadline: "",
  });

  useEffect(() => {
    axios.get(`${API}/artisans/specialties`).then((r) => setSpecialties(r.data));
  }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const optimizeDescription = async () => {
    if (!user?.id) {
      toast.error("Connectez-vous d'abord");
      return;
    }
    if (!data.description || data.description.trim().length < 10) {
      toast.error("Écrivez quelques mots d'abord, l'assistant les transformera en cahier des charges");
      return;
    }
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/ai/artisan-rewrite`, {
        user_id: user.id,
        raw_description: data.description,
        category: data.category || null,
      });
      if (r.data?.rewritten) {
        update("description", r.data.rewritten);
        toast.success(`Description optimisée ✨ (solde : ${r.data.new_balance} FCFA)`);
      }
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(err.response.data?.detail?.message || "Solde insuffisant — rechargez vos crédits");
      } else {
        toast.error("L'assistant est indisponible un instant");
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      toast.error("Connectez-vous pour publier un projet");
      navigate("/auth");
      return;
    }
    if (!data.title || !data.description || !data.category) {
      toast.error("Titre, description et catégorie sont requis");
      return;
    }
    if (!data.latitude || !data.longitude) {
      toast.error("Sélectionnez le lieu du projet sur la carte");
      return;
    }
    const min = parseInt(data.budget_min) || 0;
    const max = parseInt(data.budget_max) || 0;
    if (min <= 0 || max < min) {
      toast.error("Budget invalide");
      return;
    }

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/artisans/projects`, {
        client_id: user.id,
        title: data.title,
        description: data.description,
        category: data.category,
        budget_min: min,
        budget_max: max,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        deadline: data.deadline || null,
        photos: [],
      });
      toast.success(`Projet publié ! ${r.data.matched_artisans} artisans notifiés.`);
      navigate(`/artisans/projects/${r.data.project.id}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-12" data-testid="new-project-page">
      <div className="bg-gradient-to-br from-[#003380] via-[#003594] to-[#D4AF37] px-4 md:px-8 pt-6 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Button onClick={() => navigate("/artisans")} variant="ghost" size="sm" className="text-white hover:bg-white/20 rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Hammer className="w-7 h-7" />
            <h1 className="text-3xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
              Nouveau projet
            </h1>
          </div>
          <p className="text-white/80 text-sm">
            Décrivez votre besoin. Les meilleurs artisans recevront une notification immédiate
            et vous enverront leurs propositions.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 md:px-8 mt-6 space-y-4">
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardContent className="py-5 space-y-4">
            <div>
              <Label className="text-white/80 mb-1.5 block">Titre du projet *</Label>
              <Input
                value={data.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="Ex: Installation d'une porte en bois sur mesure"
                className="bg-transparent border-white/20 text-white"
                data-testid="title-input"
                required
              />
            </div>

            <div>
              <Label className="text-white/80 mb-1.5 block">Catégorie *</Label>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {specialties.map((s) => (
                  <button
                    type="button"
                    key={s.key}
                    onClick={() => update("category", s.key)}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                      data.category === s.key
                        ? "bg-[#D4AF37] text-[#0F172A] border-[#D4AF37]"
                        : "bg-white/5 text-white/70 border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`cat-${s.key}`}
                  >
                    <div className="text-xl mb-0.5">{s.icon}</div>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-white/80">Description *</Label>
                <button
                  type="button"
                  onClick={optimizeDescription}
                  disabled={aiLoading}
                  className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#003594]/30 to-[#D4AF37]/30 text-[#D4AF37] border border-[#D4AF37]/30 hover:from-[#003594]/50 hover:to-[#D4AF37]/50 transition-all disabled:opacity-50"
                  data-testid="ai-optimize-btn"
                >
                  <Sparkles className={`w-3 h-3 ${aiLoading ? "animate-spin" : ""}`} />
                  {aiLoading ? "Optimisation…" : "Optimiser avec l'assistant (5 FCFA)"}
                </button>
              </div>
              <Textarea
                value={data.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="Décrivez simplement votre besoin, l'assistant peut l'améliorer automatiquement"
                rows={5}
                className="bg-transparent border-white/20 text-white"
                data-testid="description-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 mb-1.5 block">Budget min (FCFA)</Label>
                <Input
                  type="number"
                  value={data.budget_min}
                  onChange={(e) => update("budget_min", e.target.value)}
                  placeholder="50000"
                  className="bg-transparent border-white/20 text-white"
                  data-testid="budget-min-input"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-1.5 block">Budget max (FCFA)</Label>
                <Input
                  type="number"
                  value={data.budget_max}
                  onChange={(e) => update("budget_max", e.target.value)}
                  placeholder="120000"
                  className="bg-transparent border-white/20 text-white"
                  data-testid="budget-max-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/80 mb-1.5 block flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                Date limite (optionnel)
              </Label>
              <Input
                type="date"
                value={data.deadline}
                onChange={(e) => update("deadline", e.target.value)}
                className="bg-transparent border-white/20 text-white"
                data-testid="deadline-input"
              />
            </div>

            <div>
              <Label className="text-white/80 mb-1.5 block flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                Lieu du projet *
              </Label>
              <LocationPicker
                onLocationSelect={(loc) => {
                  update("latitude", loc.lat);
                  update("longitude", loc.lng);
                  update("address", loc.address || "");
                }}
                placeholder="Sélectionnez l'adresse du chantier"
              />
              {data.address && (
                <p className="text-xs text-[#D4AF37] mt-1.5">📍 {data.address}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/artisans")}
            className="bg-white/5 border-white/20 text-white hover:bg-white/10"
            data-testid="cancel-btn"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-gradient-to-r from-[#003594] to-[#D4AF37] text-white font-bold hover:opacity-90"
            data-testid="submit-project-btn"
          >
            {submitting ? "Publication…" : "Publier le projet"}
          </Button>
        </div>
      </form>
    </div>
  );
}
